import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard' },
    { name: 'Monitoring', href: '/llm-monitoring' },
  ];

  const adminNavigation = user?.isAdmin
    ? [
        { name: 'Admin', href: '/admin' },
        { name: 'Users', href: '/admin/users' },
      ]
    : [];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getUserInitial = () => {
    if (user?.fullName) {
      return user.fullName.charAt(0).toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-300 h-16">
        <div className="h-full px-12 flex items-center justify-between">
          {/* Left side */}
          <div className="flex items-center gap-12">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-6 h-6 bg-black rounded flex items-center justify-center">
                <span className="text-white text-xs font-semibold">M</span>
              </div>
              <span className="text-base font-medium">MCP Platform</span>
            </Link>

            {/* Navigation items */}
            <div className="flex items-center gap-9">
              {[...navigation, ...adminNavigation].map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'text-sm transition-colors duration-200',
                    location.pathname === item.href
                      ? 'text-black font-medium'
                      : 'text-gray-600 hover:text-black'
                  )}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-6">
            {/* Search */}
            <input
              type="text"
              placeholder="Search"
              className="w-52 px-4 py-2 bg-gray-100 rounded-md text-sm outline-none transition-colors duration-200 focus:bg-gray-200 placeholder-gray-500"
            />

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white text-xs font-medium hover:bg-gray-800 transition-colors duration-200"
              >
                {getUserInitial()}
              </button>

              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-sm z-20">
                    <div className="px-4 py-3 border-b border-gray-300">
                      <p className="text-xs text-gray-500">Signed in as</p>
                      <p className="text-sm font-medium truncate">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setUserMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                      >
                        Profile
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main>
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;